import { T } from '@salecycle/experimental/Typography/Typography';
import {
  FormGroup,
  Input,
  ISelectOption,
  RadioButton,
  Select,
  SingleDatePicker,
  Toggle,
} from '@salecycle/ui-component-library';
import { useFeatureFlags } from 'core/app/AppContext';
import useNotifications from 'core/notifications/useNotifications';
import { usePreferencesState } from 'core/preferences/usePreferencesContext';
import moment, { Moment } from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PurchaseCheckData,
  PurchaseCheckUnit,
  TargetAudienceType,
} from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/CampaignEditorState';
import {
  clampValue,
  getMaxAllowedEndDate,
  getMaxDays,
  getMaxMonths,
  getMinAllowedDate,
  validateAndNormalizeEndDate,
  validateAndNormalizeStartDate,
} from 'sections/bulk-retargeting-campaigns/CampaignEditor/utils/dateValidation';
import styled, { useTheme } from 'styled-components';

const PurchaseCheckBackground = styled.div`
  background-color: ${({ theme }) => theme.designToken.neutral50};
  padding: ${({ theme }) => theme.designToken.spacing5};
  display: flex;
  gap: ${({ theme }) => theme.designToken.spacing2};
  align-items: center;
`;

const ItalicHelperText = styled.div`
  font-style: italic;
`;
export interface PurchaseCheckProps {
  onChange: (purchaseCheck: PurchaseCheckData) => void;
  value: PurchaseCheckData;
  disabled?: boolean;
}

export const PurchaseCheck = ({ onChange, value, disabled }: PurchaseCheckProps) => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();
  const { timezone } = usePreferencesState();
  const targetAudience = value.targetAudience;

  const { flagEnabled } = useFeatureFlags();
  const featureBulkDatePickers = flagEnabled('ld-bulk-date-pickers');

  const [actualNumberOfDays, setActualNumberOfDays] = useState(value.purchaseCheckDays);

  const setPurchaseCheckCallback = useCallback(
    (checked: boolean) => {
      onChange({
        targetAudience: value.targetAudience,
        enablePurchaseCheck: checked,
        purchaseCheckDays: value.purchaseCheckDays,
        purchaseCheckMonths: value.purchaseCheckMonths,
        purchaseCheckUnit: value.purchaseCheckUnit,
        purchaseCheckStartDate: value.purchaseCheckStartDate,
        purchaseCheckEndDate: value.purchaseCheckEndDate,
        isPurchaseCheckUsingDateRange: value.isPurchaseCheckUsingDateRange,
      });
    },
    [onChange, value],
  );

  const onSelectedDaysChanged = useCallback(
    (days: string) => {
      let newValue = Number(days);
      if (Number(days) <= 1) {
        newValue = 1;
      }

      if (Number(days) >= 30) {
        newValue = 30;
      }

      onChange({
        targetAudience: value.targetAudience,
        enablePurchaseCheck: value.enablePurchaseCheck,
        purchaseCheckDays: newValue,
        purchaseCheckMonths: value.purchaseCheckMonths,
        purchaseCheckUnit: value.purchaseCheckUnit,
        purchaseCheckStartDate: value.purchaseCheckStartDate,
        purchaseCheckEndDate: value.purchaseCheckEndDate,
        isPurchaseCheckUsingDateRange: value.isPurchaseCheckUsingDateRange,
      });
    },
    [onChange, value],
  );

  const targetIsPurchases = targetAudience?.audience === TargetAudienceType.Purchase;
  const isToggleDisabled = targetIsPurchases || disabled;

  const [isStartDateFocused, setIsStartDateFocused] = useState(false);
  const [isEndDateFocused, setIsEndDateFocused] = useState(false);

  // supporting methods for Bulk retargeting date range picker feature and months, days logic implementation
  const minAllowedDate = useMemo(() => getMinAllowedDate(timezone), [timezone]);
  const maxAllowedEndDate = useMemo(() => {
    if (!value.purchaseCheckStartDate) {
      return undefined;
    }
    return getMaxAllowedEndDate(value.purchaseCheckStartDate);
  }, [value.purchaseCheckStartDate]);

  const maxDays = useMemo(() => getMaxDays(timezone), [timezone]);
  const maxMonths = useMemo(() => getMaxMonths(timezone), [timezone]);

  const { showNotification } = useNotifications();

  const unitOptions: ISelectOption<PurchaseCheckUnit>[] = useMemo(
    () => [
      {
        label: t('bulkRetargetingCampaigns.editor.purchaseCheckUnit.days'),
        value: PurchaseCheckUnit.Days,
      },
      {
        label: t('bulkRetargetingCampaigns.editor.purchaseCheckUnit.months'),
        value: PurchaseCheckUnit.Months,
      },
    ],
    [t],
  );

  const onWithinClick = useCallback(() => {
    onChange({
      targetAudience: value.targetAudience,
      enablePurchaseCheck: value.enablePurchaseCheck,
      purchaseCheckDays: value.purchaseCheckDays,
      purchaseCheckMonths: value.purchaseCheckMonths,
      purchaseCheckUnit: value.purchaseCheckUnit,
      purchaseCheckStartDate: value.purchaseCheckStartDate,
      purchaseCheckEndDate: value.purchaseCheckEndDate,
      isPurchaseCheckUsingDateRange: false,
    });
  }, [onChange, value]);

  const onBetweenClick = useCallback(() => {
    onChange({
      targetAudience: value.targetAudience,
      enablePurchaseCheck: value.enablePurchaseCheck,
      purchaseCheckDays: value.purchaseCheckDays,
      purchaseCheckMonths: value.purchaseCheckMonths,
      purchaseCheckUnit: value.purchaseCheckUnit,
      purchaseCheckStartDate: value.purchaseCheckStartDate,
      purchaseCheckEndDate: value.purchaseCheckEndDate,
      isPurchaseCheckUsingDateRange: true,
    });
  }, [onChange, value]);

  const onSelectedValueChanged = useCallback(
    (newValue: string) => {
      const maxValue = value.purchaseCheckUnit === PurchaseCheckUnit.Days ? maxDays : maxMonths;
      const clampedValue = clampValue(newValue, maxValue);
      onChange({
        targetAudience: value.targetAudience,
        enablePurchaseCheck: value.enablePurchaseCheck,
        purchaseCheckDays: value.purchaseCheckUnit === PurchaseCheckUnit.Days ? clampedValue : value.purchaseCheckDays,
        purchaseCheckMonths:
          value.purchaseCheckUnit === PurchaseCheckUnit.Months ? clampedValue : value.purchaseCheckMonths,
        purchaseCheckUnit: value.purchaseCheckUnit,
        purchaseCheckStartDate: value.purchaseCheckStartDate,
        purchaseCheckEndDate: value.purchaseCheckEndDate,
        isPurchaseCheckUsingDateRange: value.isPurchaseCheckUsingDateRange,
      });
    },
    [onChange, value, maxDays, maxMonths],
  );

  const selectedUnit: ISelectOption<PurchaseCheckUnit> = useMemo(() => {
    // Always ensure we have a valid unit, default to Days if not set
    const unit = value.purchaseCheckUnit || PurchaseCheckUnit.Days;
    const currentValue = unit === PurchaseCheckUnit.Days ? value.purchaseCheckDays : value.purchaseCheckMonths;
    const isSingular = currentValue === 1;

    // Find the base option to ensure the value property matches (React Select matches by value, not label)
    const baseOption = unitOptions.find(option => option.value === unit) || unitOptions[0];

    // Customize the label based on singular/plural while keeping the same value property
    // This ensures React Select can match it while displaying the correct singular/plural form
    if (unit === PurchaseCheckUnit.Days) {
      return {
        ...baseOption,
        label: isSingular
          ? t('bulkRetargetingCampaigns.editor.purchaseCheckUnit.day')
          : t('bulkRetargetingCampaigns.editor.purchaseCheckUnit.days'),
      };
    } else if (unit === PurchaseCheckUnit.Months) {
      return {
        ...baseOption,
        label: isSingular
          ? t('bulkRetargetingCampaigns.editor.purchaseCheckUnit.month')
          : t('bulkRetargetingCampaigns.editor.purchaseCheckUnit.months'),
      };
    }

    return baseOption;
  }, [unitOptions, value.purchaseCheckUnit, value.purchaseCheckDays, value.purchaseCheckMonths, t]);

  useEffect(() => {
    if (value.isPurchaseCheckUsingDateRange && value.purchaseCheckStartDate && value.purchaseCheckEndDate) {
      setActualNumberOfDays(value.purchaseCheckEndDate.diff(value.purchaseCheckStartDate, 'days'));
    } else {
      if (value.purchaseCheckUnit === PurchaseCheckUnit.Days) {
        setActualNumberOfDays(value.purchaseCheckDays);
      } else {
        setActualNumberOfDays(value.purchaseCheckMonths * 30);
      }
    }
  }, [
    value.isPurchaseCheckUsingDateRange,
    value.purchaseCheckDays,
    value.purchaseCheckEndDate,
    value.purchaseCheckMonths,
    value.purchaseCheckStartDate,
    value.purchaseCheckUnit,
  ]);

  const onUnitChanged = useCallback(
    (selected: ISelectOption<PurchaseCheckUnit>) => {
      // When switching units, reset the value to 1 (default) for the new unit
      // provides a clean state when switching
      onChange({
        targetAudience: value.targetAudience,
        enablePurchaseCheck: value.enablePurchaseCheck,
        purchaseCheckDays: selected.value === PurchaseCheckUnit.Days ? 1 : value.purchaseCheckDays,
        purchaseCheckMonths: selected.value === PurchaseCheckUnit.Months ? 1 : value.purchaseCheckMonths,
        purchaseCheckUnit: selected.value,
        purchaseCheckStartDate: value.purchaseCheckStartDate,
        purchaseCheckEndDate: value.purchaseCheckEndDate,
        isPurchaseCheckUsingDateRange: value.isPurchaseCheckUsingDateRange,
      });
    },
    [onChange, value],
  );

  const onStartDateChanged = useCallback(
    (selectedDate: Moment | null) => {
      const normalizedStartDate = validateAndNormalizeStartDate(
        selectedDate,
        value.purchaseCheckEndDate,
        minAllowedDate,
        timezone,
      );

      // If validation failed, don't update
      if (selectedDate && !normalizedStartDate) {
        return;
      }

      let finalEndDate = value.purchaseCheckEndDate;

      // If there's an existing end date and a new start date, check if it exceeds the 180 day limit
      if (normalizedStartDate && value.purchaseCheckEndDate) {
        const daysDifference = value.purchaseCheckEndDate.diff(normalizedStartDate, 'days');
        if (daysDifference > 180) {
          // Auto-adjust the end date to be within the 180 day range
          const maxAllowedEndDate = normalizedStartDate.clone().add(180, 'days');
          finalEndDate = maxAllowedEndDate.clone().endOf('day');
          showNotification(
            t('bulkRetargetingCampaigns.editor.purchaseCheckEndDateValidation.' + value.targetAudience.audience),
            'secondary',
            10000,
          );
        }
      }

      onChange({
        targetAudience: value.targetAudience,
        enablePurchaseCheck: value.enablePurchaseCheck,
        purchaseCheckDays: value.purchaseCheckDays,
        purchaseCheckMonths: value.purchaseCheckMonths,
        purchaseCheckUnit: value.purchaseCheckUnit,
        purchaseCheckStartDate: normalizedStartDate,
        purchaseCheckEndDate: finalEndDate,
        isPurchaseCheckUsingDateRange: value.isPurchaseCheckUsingDateRange,
      });
    },
    [onChange, value, timezone, minAllowedDate, showNotification, t],
  );

  const onEndDateChanged = useCallback(
    (selectedDate: Moment | null) => {
      if (!selectedDate) {
        onChange({
          targetAudience: value.targetAudience,
          enablePurchaseCheck: value.enablePurchaseCheck,
          purchaseCheckDays: value.purchaseCheckDays,
          purchaseCheckMonths: value.purchaseCheckMonths,
          purchaseCheckUnit: value.purchaseCheckUnit,
          purchaseCheckStartDate: value.purchaseCheckStartDate,
          purchaseCheckEndDate: null,
          isPurchaseCheckUsingDateRange: value.isPurchaseCheckUsingDateRange,
        });
        return;
      }

      // Clamp the selected date to maxAllowedEndDate if it exceeds it
      let dateToValidate = selectedDate;
      if (value.purchaseCheckStartDate && selectedDate.isAfter(maxAllowedEndDate, 'day')) {
        dateToValidate = maxAllowedEndDate;
      }

      const normalizedEndDate = validateAndNormalizeEndDate(
        dateToValidate,
        value.purchaseCheckStartDate,
        minAllowedDate,
        maxAllowedEndDate,
        timezone,
      );

      // If validation failed, don't update
      if (!normalizedEndDate) {
        return;
      }

      onChange({
        targetAudience: value.targetAudience,
        enablePurchaseCheck: value.enablePurchaseCheck,
        purchaseCheckDays: value.purchaseCheckDays,
        purchaseCheckMonths: value.purchaseCheckMonths,
        purchaseCheckUnit: value.purchaseCheckUnit,
        purchaseCheckStartDate: value.purchaseCheckStartDate,
        purchaseCheckEndDate: normalizedEndDate,
        isPurchaseCheckUsingDateRange: value.isPurchaseCheckUsingDateRange,
      });
    },
    [
      value.purchaseCheckStartDate,
      value.targetAudience,
      value.enablePurchaseCheck,
      value.purchaseCheckDays,
      value.purchaseCheckMonths,
      value.purchaseCheckUnit,
      value.isPurchaseCheckUsingDateRange,
      maxAllowedEndDate,
      minAllowedDate,
      timezone,
      onChange,
    ],
  );

  return targetIsPurchases ? (
    <PurchaseCheckBackground>
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing2 }}>
        <T variant="headingH3">{t('bulkRetargetingCampaigns.editor.madeAPurchase')}</T>
        <T variant="body1">{t('bulkRetargetingCampaigns.editor.purchaseCheckPostPurchaseMessage')}</T>
      </div>
    </PurchaseCheckBackground>
  ) : (
    <PurchaseCheckBackground>
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing3 }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: theme.designToken.spacing3, alignItems: 'center' }}>
          <T variant="headingH3">
            {targetAudience.audience === TargetAudienceType.BasketAbandonment &&
              t('bulkRetargetingCampaigns.editor.abandonedABasket')}
            {targetAudience.audience === TargetAudienceType.BrowseAbandonment &&
              t('bulkRetargetingCampaigns.editor.browsedWithoutPurchasing')}
            {targetAudience.audience === TargetAudienceType.Purchase &&
              t('bulkRetargetingCampaigns.editor.madeAPurchase')}
          </T>
          <Toggle
            inputId="purchase-check-toggle"
            ariaLabel={t('bulkRetargetingCampaigns.editor.purchaseCheckToggleAria')}
            checked={value.enablePurchaseCheck}
            onChange={setPurchaseCheckCallback}
            margin={false}
            disabled={isToggleDisabled}
          />
        </div>
        {value.enablePurchaseCheck && (
          <>
            {featureBulkDatePickers ? (
              <FormGroup>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing5 }}>
                  <span
                    style={{
                      display: 'flex',
                      gap: theme.designToken.spacing5,
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <RadioButton
                      selected={!value.isPurchaseCheckUsingDateRange}
                      onClick={onWithinClick}
                      disabled={targetIsPurchases || disabled}
                      data-testid="purchase-check-within"
                    />
                    <T>{t('bulkRetargetingCampaigns.editor.purchaseCheckDescriptionStart')}</T>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: theme.designToken.spacing2,
                        verticalAlign: 'middle',
                      }}
                    >
                      <Input
                        inputId="purchase-check-value"
                        type="number"
                        spacingBelow={false}
                        max={value.purchaseCheckUnit === PurchaseCheckUnit.Days ? String(maxDays) : String(maxMonths)}
                        min="1"
                        value={
                          value.purchaseCheckUnit === PurchaseCheckUnit.Days
                            ? value.purchaseCheckDays
                            : value.purchaseCheckMonths
                        }
                        onChange={onSelectedValueChanged}
                        disabled={
                          !value.enablePurchaseCheck ||
                          targetIsPurchases ||
                          disabled ||
                          value.isPurchaseCheckUsingDateRange
                        }
                        aria-label={t('bulkRetargetingCampaigns.editor.purchaseCheckDaysAria')}
                        style={{ width: 85 }}
                      />
                      <span style={{ width: 128, height: 'fit-content' }}>
                        <Select
                          inputId="purchase-check-unit"
                          inline
                          spacingBelow={false}
                          value={selectedUnit}
                          onChange={onUnitChanged}
                          options={unitOptions}
                          ariaLabel={t('bulkRetargetingCampaigns.editor.purchaseCheckUnitAria')}
                          disabled={
                            !value.enablePurchaseCheck ||
                            targetIsPurchases ||
                            disabled ||
                            value.isPurchaseCheckUsingDateRange
                          }
                        />
                      </span>
                    </span>
                    <T>{t('bulkRetargetingCampaigns.editor.purchaseCheckEndDescription')}</T>
                    {!value.isPurchaseCheckUsingDateRange && (
                      <ItalicHelperText>
                        {t(
                          actualNumberOfDays === 1
                            ? 'bulkRetargetingCampaigns.editor.targetAudienceSegmentation.italicTextDay'
                            : 'bulkRetargetingCampaigns.editor.targetAudienceSegmentation.italicTextDays',
                          { actualNumberOfDays },
                        )}
                      </ItalicHelperText>
                    )}
                  </span>
                  <span
                    style={{
                      display: 'flex',
                      gap: theme.designToken.spacing5,
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <RadioButton
                      selected={value.isPurchaseCheckUsingDateRange}
                      onClick={onBetweenClick}
                      disabled={targetIsPurchases || disabled}
                      data-testid="purchase-check-between"
                    />
                    <T>between </T>
                    <span data-testid="purchase-check-start-date">
                      <SingleDatePicker
                        ariaLabel={t('bulkRetargetingCampaigns.editor.purchaseCheckStartDateLabel')}
                        date={value.purchaseCheckStartDate}
                        focused={isStartDateFocused}
                        openDirection="up"
                        isPastDatesOnly={true}
                        isMini={false}
                        onDateChange={onStartDateChanged}
                        onFocusChange={({ focused }) => setIsStartDateFocused(focused)}
                        placeholder={'DD/MM/YYYY'}
                        spacingBelow={false}
                        width={140}
                        minDate={minAllowedDate}
                        maxDate={value.purchaseCheckEndDate}
                        disabled={targetIsPurchases || disabled || !value.isPurchaseCheckUsingDateRange}
                      />
                    </span>
                    <T> and </T>
                    <span data-testid="purchase-check-end-date">
                      <SingleDatePicker
                        ariaLabel={t('bulkRetargetingCampaigns.editor.purchaseCheckEndDateLabel')}
                        date={value.purchaseCheckEndDate}
                        focused={isEndDateFocused}
                        openDirection="up"
                        isPastDatesOnly={true}
                        isMini={false}
                        onDateChange={onEndDateChanged}
                        onFocusChange={({ focused }) => setIsEndDateFocused(focused)}
                        placeholder={'DD/MM/YYYY'}
                        spacingBelow={false}
                        width={140}
                        minDate={
                          value.purchaseCheckStartDate
                            ? moment.max(value.purchaseCheckStartDate, minAllowedDate)
                            : minAllowedDate
                        }
                        maxDate={maxAllowedEndDate}
                        disabled={targetIsPurchases || disabled || !value.isPurchaseCheckUsingDateRange}
                      />
                    </span>
                    {value.isPurchaseCheckUsingDateRange &&
                      value.purchaseCheckStartDate &&
                      value.purchaseCheckEndDate && (
                        <ItalicHelperText>
                          {t(
                            actualNumberOfDays === 1
                              ? 'bulkRetargetingCampaigns.editor.targetAudienceSegmentation.italicTextDay'
                              : 'bulkRetargetingCampaigns.editor.targetAudienceSegmentation.italicTextDays',
                            { actualNumberOfDays },
                          )}
                        </ItalicHelperText>
                      )}
                  </span>
                </div>
              </FormGroup>
            ) : (
              <div style={{ display: 'flex', gap: theme.designToken.spacing3, alignItems: 'center' }}>
                {value.enablePurchaseCheck && (
                  <div style={{ display: 'flex', gap: theme.designToken.spacing1, alignItems: 'center' }}>
                    <>{t('bulkRetargetingCampaigns.editor.purchaseCheckDescriptionStart')}</>
                    <span style={{ width: 88, height: 'fit-content' }}>
                      <Input
                        inputId="purchase-check-days"
                        type="number"
                        spacingBelow={false}
                        max="30"
                        min="1"
                        value={value.purchaseCheckDays}
                        onChange={onSelectedDaysChanged}
                        disabled={!value.enablePurchaseCheck || targetIsPurchases || disabled}
                        aria-label={t('bulkRetargetingCampaigns.editor.purchaseCheckDaysAria')}
                        style={{ width: 65 }}
                      />
                    </span>
                    <>{t('bulkRetargetingCampaigns.editor.purchaseCheckDescriptionEnd')}</>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </PurchaseCheckBackground>
  );
};
